import bubbleDialogTemplate from './bubble-dialog.tpl.html';
import bubbleDialogMenuTemplate from './bubble-dialog-menu.tpl.html';
import './bubble-dialog.less';
import * as EventUtils from '../../utils/event-utils';
import { stopEvent } from '../../utils/event-utils';
import { positionHelperModule } from '../../helpers/position-helper/position-helper';
import { ngSanitizeModule } from '../../vendor/angular-modules';
import { scrollToComponentModule } from '../scroll-to/scroll-to';
import { keycodesModule } from '../../constants/keycodes';

let _compiledTemplate;
let _compiledMenuTemplate;

export const bubbleDialogComponentModule = angular
  .module('Q.components.bubble-dialog', [
    ngSanitizeModule,
    positionHelperModule,
    scrollToComponentModule,
    keycodesModule,
  ])

  /**
   * @docType shared
   * @category service
   * @name Bubble-Dialog-Service
   * @description
   * A service that allows you to create a bubble dialog
   * @returns A factory object {object}
   */
  .factory(
    'bubbleDialogService',
    function (
      $compile,
      $rootScope,
      $templateRequest,
      $timeout,
      $q,
      $document,
      $window,
      $animate,
      positionHelper,
      scrollToElement,
      KeyCodes,
    ) {
      /**
       * @parent Bubble-Dialog-Service
       * @name open
       * @description
       * A method that will open a bubble dialog on an element
       * @param scope {$rootScope.Scope} The angular scope to use for the bubble dialog's content (will be inherited)
       * @param {Object} el An element to position the bubble dialog relative to
       * @param {Object} opts A series of options that allow you configure the bubble dialog (title, content, buttons, position)
       * @param {string?} opts.content Optional content for the dialog (HTML OK) (req'd if no template)
       * @param {string?} opts.contentTemplate Optional content as template for the dialog (will be compiled)
       * @param {string?} opts.template Optional complete template for the dialog (must include all of the popover elements)
       * @param {string?} opts.menu Optional menu template for the dialog, which is used to render the bubble as a dropdown menu (handles layout, styling)
       * @param {string?} opts.position Optional text value indicating where to show the popup. Default is above or below based on how close `el` is to the window edge
       * @param {string?} opts.positionArrow If provided, use to center the tooltip
       * @param {boolean?} opts.closeable If true, then show a close button when hovering over the bubble
       * @param {string?} opts.title Optional title for the dialog
       * @param {string?} opts.className Optional class name to add to the dialog
       * @param {Array<Object>?} opts.buttons Optional buttons to append to the end
       * @param {number?} opts.delay If provided, delays showing the bubble for delay milliseconds
       * @param {number?} opts.estimatedHeight If provided, will be used to help determine default placement (top or bottom)
       * @param {Object?} opts.mousePosition If provided, anchors the bubble around this {pageX,pageY} point
       * @param {boolean?|string?} opts.autofocus If provided, enables focusing on the menu and keyboard navigation. If a string, used as the selector for the initial item
       * @returns {object} object representative of the bubble dialog instance just opened {object}
       */
      function open(scope, el, opts) {
        el = angular.element(el);
        let position =
          opts.position || positionHelper.topOrBottom(el, opts.estimatedHeight);
        let delayTimeout = null;
        const cancelDelayedRender = () => {
          if (delayTimeout) {
            $timeout.cancel(delayTimeout);
            delayTimeout = null;
          }
        };

        let existingBubbleInstance = el.data('bubbleDialog');
        if (
          existingBubbleInstance &&
          existingBubbleInstance.scope &&
          !existingBubbleInstance.closed
        ) {
          if (!opts.buttons && !opts.template && !opts.menu) {
            // just update scope
            existingBubbleInstance.scope.title = opts.title;
            existingBubbleInstance.scope.content = opts.content;
            existingBubbleInstance.scope.contentTemplate = opts.contentTemplate;
            existingBubbleInstance.scope.position = position;
            return existingBubbleInstance;
          } else {
            // destroy the existing bubble (can't have multiple on an element)
            existingBubbleInstance.closeImmediately();
          }
        }

        let bubbleScope;
        let bubbleEl;
        let stateChangeDereg;
        let scopeDereg;
        let deferred = $q.defer();

        let bubbleInstance = {
          scope: null,
          bubbleElement: null,
          target: el[0],
          showNow() {
            if (!bubbleInstance.rendering) {
              renderBubble();
            }
          },
          close() {
            bubbleInstance.closed = true;
            stateChangeDereg && stateChangeDereg();
            scopeDereg && scopeDereg();
            cancelDelayedRender();
            if (bubbleScope) {
              bubbleScope.remove();
            } else if (bubbleEl) {
              // ensure the node isn't stuck on screen for some reason
              bubbleEl.remove();
            }
            // remove from element
            el.data('bubbleDialog', null);
            deferred.reject('closed');
          },
          closeImmediately() {
            bubbleInstance.immediate = true;
            bubbleInstance.close();
          },
          result: deferred.promise,
        };
        // never persist across page changes
        stateChangeDereg = scope.$on(
          '$stateChangeStart',
          bubbleInstance.closeImmediately,
        );
        scopeDereg = scope.$on('$destroy', bubbleInstance.closeImmediately);

        function setHandlers(enable) {
          let method = enable ? 'on' : 'off';
          bubbleEl[method]('click touchstart', EventUtils.stopBubbling);
          // Removed because it was causing unexpected behavior with tooltips nested inside links
          // Could not resolve the original reasoning behind stopping bubbling
          // el[method]("click touchstart", EventUtils.stopBubbling);
          $document[method]('keydown', escapeKeyDown);
          $document[method]('mousedown', escapeClick);
          // on mobile, these functions make sure we don't close on scroll (or long press) events
          $document[method]('touchstart', escapeTouchStart);
          $document[method]('touchend', escapeTouchEnd);
        }

        function isOutsideNode(event) {
          const target = event.target;
          const bubbleNode = bubbleInstance.bubbleElement;
          return (
            target &&
            bubbleNode &&
            !bubbleNode[0].contains(target) &&
            !el[0].contains(target)
          );
        }

        function escapeKeyDown(event) {
          if (event.which === 27) {
            deferred.reject('escape key press');
          }
        }

        function escapeClick(event) {
          if (isOutsideNode(event)) {
            deferred.reject('user clicked elsewhere');
          }
        }

        let touchStartTime = -Infinity;

        function escapeTouchStart() {
          touchStartTime = Date.now();
        }

        function escapeTouchEnd(event) {
          if (Date.now() - touchStartTime < 150) {
            escapeClick(event);
          }
        }

        function renderBubble() {
          if (bubbleInstance.closed || !bubbleInstance.compiledTemplate) return;
          bubbleInstance.rendering = true;
          cancelDelayedRender();
          el.addClass('bubble-showing');

          bubbleInstance.scope = bubbleScope = scope.$new();
          bubbleScope.closeable = opts.closeable;
          bubbleScope.title = opts.title;
          bubbleScope.content = opts.content;
          bubbleScope.contentTemplate = opts.contentTemplate;
          bubbleScope.buttons = opts.buttons;
          bubbleScope.className =
            (opts.className || '') + (opts.title ? ' titled' : '');
          bubbleScope.menu = opts.menu;
          bubbleScope.position = position;
          bubbleScope.positionClass = position.split('-')[0] + ' ' + position;
          bubbleScope.removing = false;

          bubbleScope.remove = () => {
            if (bubbleScope.removing) return;
            el.removeClass('bubble-showing');

            bubbleScope.removing = true;

            if (bubbleEl) {
              setHandlers(false);
              bubbleScope.$applyAsync(() => {
                if (bubbleInstance.immediate) {
                  bubbleEl.remove();
                  bubbleScope.$destroy();
                } else {
                  $animate.removeClass(bubbleEl, 'showing').then(() => {
                    bubbleEl.remove();
                    bubbleScope.$destroy();
                  });
                }
              });
            } else {
              bubbleScope.$destroy();
            }
          };
          deferred.promise.finally(bubbleInstance.close);

          bubbleInstance.compiledTemplate(bubbleScope, function (_bubbleEl) {
            if (bubbleInstance.closed) return;

            bubbleInstance.bubbleElement = bubbleEl = _bubbleEl;

            let $body = angular.element($document[0].body);

            $body.append(bubbleEl);

            $window.requestAnimationFrame(() => {
              positionHelper.autoPositionPopup(
                bubbleScope,
                opts.positionArrow || el,
                bubbleEl,
                position,
                {
                  hasArrow: true,
                  mousePosition: opts.mousePosition,
                },
              );
            });

            if (opts.menu) {
              // auto close on click for menus
              bubbleEl.on('click', bubbleInstance.close);
            }

            $animate.addClass(bubbleEl, 'showing').then(() => {
              bubbleInstance.rendering = true;
              deferred.notify({
                ready: true,
                element: bubbleEl,
              });
            });

            if (opts.autofocus) {
              // focuses in on the first selectable item
              // Note: this only works well if everything has an explicit `tabindex`
              const selector = angular.isString(opts.autofocus)
                ? opts.autofocus
                : '[tabindex]';
              $timeout(
                () => {
                  bubbleEl.querySelector(selector).focus();
                  scrollToElement({
                    target: opts.positionArrow || el,
                    ignoreIfVisible: true,
                    center: true,
                  });

                  // setup keystrokes
                  bubbleEl
                    .querySelectorAll('[tabindex]')
                    .on('keydown', (event) => {
                      const target = event.target;
                      if (
                        !target ||
                        KeyCodes.isCommand(event) ||
                        KeyCodes.isShift(event) ||
                        KeyCodes.isAlt(event)
                      ) {
                        return;
                      }
                      const which = event.which;
                      if (which === KeyCodes.ENTER) {
                        stopEvent(event);
                        angular.element(target).triggerHandler('click');
                      } else if (
                        which === KeyCodes.DOWN_ARROW ||
                        which === KeyCodes.UP_ARROW
                      ) {
                        const nodes = bubbleEl.querySelectorAll('[tabindex]');
                        const idx =
                          Array.from(nodes).indexOf(target) +
                          (which === KeyCodes.DOWN_ARROW ? 1 : -1);
                        const focusNode = nodes[idx];
                        if (focusNode && focusNode.focus) {
                          stopEvent(event);
                          focusNode.focus();
                        }
                      }
                    });
                },
                100,
                false,
              );
            }

            setHandlers(true);
          });
        }

        function setupBubble() {
          delayTimeout = $timeout(renderBubble, opts.delay || 0);
        }

        let compiledTemplate = _compiledTemplate;
        if (opts.template) {
          compiledTemplate = $compile(opts.template);
        } else if (opts.menu) {
          if (!_compiledMenuTemplate) {
            _compiledMenuTemplate = $compile(bubbleDialogMenuTemplate);
          }
          compiledTemplate = _compiledMenuTemplate;
        }
        if (!compiledTemplate) {
          compiledTemplate = _compiledTemplate = $compile(bubbleDialogTemplate);
        }
        bubbleInstance.compiledTemplate = compiledTemplate;
        $timeout(setupBubble, 0);

        el.data('bubbleDialog', bubbleInstance);

        if (opts.buttons) {
          opts.buttons.forEach(function (button) {
            button.resolve = function ($event) {
              EventUtils.stopEvent($event);
              deferred.resolve(button.title);
            };
          });
        }

        return bubbleInstance;
      }

      return {
        open,
      };
    },
  )
  /**
   * @ngdoc directive
   * @docType shared
   * @category directive
   * @name bubbleDialog
   * @restrict A
   * @element ANY
   * @description
   * Pops up a bubble dialog window with a message.
   *
   * @param {expression} bubbleDialog Message to show
   * @param {expression} bubbleDialogTemplate content as template to show (overrides content)
   * @param {template} bubbleDialogTitle Optional title
   * @param {template} bubbleDialogPosition Optional position argument
   *
   * @returns Directive Definition Object {object}
   */
  .directive('bubbleDialog', function (bubbleDialogService) {
    const ACTIVE_CLASS = 'bubble-dialog-active';
    return {
      restrict: 'A',
      require: '?^tooltip',
      link(scope, element, attrs, tooltipCtrl) {
        let instance = null;

        element.on('click', function (event) {
          EventUtils.stopEvent(event);

          if (instance && !instance.closed) {
            instance.close();
            instance = null;
            if (tooltipCtrl) tooltipCtrl.setDisabled(false);
            element.removeClass(ACTIVE_CLASS);
          } else {
            if (tooltipCtrl) tooltipCtrl.setDisabled(true);
            element.addClass(ACTIVE_CLASS);

            instance = bubbleDialogService.open(scope, element, {
              title: attrs.bubbleDialogTitle,
              content: scope.$eval(attrs.bubbleDialog),
              contentTemplate: scope.$eval(attrs.bubbleDialogTemplate),
              position: attrs.bubbleDialogPosition || undefined,
              className: `bubble-dialog-simple ${attrs.bubbleDialogClassName || ''}`,
            });
          }
        });
      },
    };
  });
